import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { AgentUserQuery } from 'interfaceAdapter/queries/AgentUser';
import { AgentRegistrationProfileInfoQuery } from 'interfaceAdapter/queries/AgentRegistrationProfileInfo';
import { AgentEditRepository } from 'interfaceAdapter/repositories/AgentEdit';
import { CompanySearchResult } from 'domain/entities/CompanySearchResult/CompanySearchResult';
import { useNavigate } from 'react-router-dom';
import { useLoading } from 'application/contexts/useLoading';
import { AgentEditSchema } from 'interfaceAdapter/gatewaySchemas/IAgentEditClient';
import { agentUserContext } from 'application/contexts/useAgentUser';
import { IIndustry } from 'domain/entities/Industry/Industry';

type AgentForm = yup.InferType<typeof AgentEditSchema>;

export const useAgentProfileRegistration = () => {
    const diContainer = useContext(diContainerContext);
    const agentRegistrationProfileInfoQuery = diContainer.resolve(AgentRegistrationProfileInfoQuery);
    const agentUserQuery = diContainer.resolve(AgentUserQuery);
    const agentEditRepository = diContainer.resolve(AgentEditRepository);
    const { loading, setLoading } = useLoading();
    const { user } = useContext(agentUserContext);
    const [areaChoices, setAreaChoices] = useState<{ value: string; label: string }[]>([]);
    const [employeeChoices, setEmployeeChoices] = useState<string[][]>([]);
    const [revenueChoices, setRevenueChoices] = useState<string[][]>([]);
    const [industryChoices, setIndustryChoices] = useState<{ value: string; label: string }[]>([]);
    const [agentTypeChoices, setAgentTypeChoices] = useState<{ value: string; label: string }[]>([]);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const navigate = useNavigate();

    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        watch,
        setValue,
    } = useForm<AgentForm>({
        resolver: yupResolver(AgentEditSchema),
        mode: 'onChange',
        defaultValues: {
            name: '',
        },
    });

    useEffect(() => {
        const checkCanEnter = async () => {
            const { status } = await agentUserQuery.checkToken(true);

            if (status === false) {
                // 有効なトークンが無いのでリダイレクト
                navigate('/agent/login/');
            }
        };
        checkCanEnter();
    }, [window.location]);

    useEffect(() => {
        const fetchData = async () => {
            const data = await agentRegistrationProfileInfoQuery.fetch();
            setAreaChoices((data.area_choices as [string, string][]).map(([value, label]) => ({ value, label })));
            setEmployeeChoices(data.employee_choices);
            setRevenueChoices(data.revenue_choices);
            setIndustryChoices(
                data.industries.map((industry: IIndustry) => ({ value: industry.id.toString(), label: industry.name })),
            );
            setAgentTypeChoices(
                (data.agent_type_choices as [string, string][]).map(([value, label]) => ({ value, label })),
            );
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (user?.agent?.name) {
            navigate('/agent/registration/user_profile/');
        }
    }, [user]);

    const submit = handleSubmit(async (data) => {
        setLoading(true);
        try {
            await agentEditRepository.edit({
                ...data,
            });
            setShowSnackbar(true);
            setTimeout(() => window.location.replace('/agent/registration/user_profile/'), 1500);
        } finally {
            setLoading(false);
        }
    });

    const onCompanySelect = (company: CompanySearchResult) => {
        setValue('name', company.name);
        setValue('company_code', company.number);
        setValue('billing_zipcode', company.zip_code);
        setValue('billing_address', company.address);
    };

    return {
        control,
        errors,
        loading,
        showSnackbar,
        areaChoices,
        employeeChoices,
        revenueChoices,
        industryChoices,
        agentTypeChoices,
        setShowSnackbar,
        submit,
        getValues,
        setValue,
        watch,
        onCompanySelect,
    };
};
